import { useEffect, useState } from "react";
import { Sparkles, Loader2, ArrowRight, Gauge, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SECTION_LABELS = {
  hero: "Hero",
  chapters: "Manifesto",
  metodo: "Metodo",
  servizi: "Soluzioni",
  target: "Clienti",
  partners: "Partner",
  footer: "Footer",
  seo: "SEO (meta tag)",
};

const barColor = (s) => (s >= 80 ? "bg-mint" : s >= 50 ? "bg-gold" : "bg-red-400");
const textColor = (s) => (s >= 80 ? "text-mint" : s >= 50 ? "text-gold" : "text-red-400");

export default function CmsHome({ onImprove, onSelect }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [sitemapBusy, setSitemapBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.post("/seo/analyze?ai=false");
      setData(data);
    } catch {
      setData({ sections: {}, global: 0 });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const improve = async () => {
    setBusy(true);
    try {
      await onImprove();
      toast.success("Suggerimenti AI applicati e salvati");
      await load();
    } catch {
      toast.error("Analisi AI non riuscita. Riprova tra poco.");
    } finally {
      setBusy(false);
    }
  };

  const regenerateSnapshot = async () => {
    setRegenBusy(true);
    try {
      await api.post("/seo/regenerate-snapshot");
      toast.success("Snapshot SEO/AI rigenerato — i motori AI vedono i contenuti aggiornati");
    } catch {
      toast.error("Rigenerazione non riuscita. Riprova tra poco.");
    } finally {
      setRegenBusy(false);
    }
  };

  return (
    <div data-testid="cms-home">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl font-bold">Panoramica contenuti</h1>
          <p className="mt-1 text-xs text-muted-foreground">Punteggio qualità/SEO per ogni sezione del sito, calcolato sui contenuti salvati.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="cms-regenerate-sitemap"
            onClick={async () => {
              setSitemapBusy(true);
              try {
                await api.post("/seo/regenerate-sitemap");
                toast.success("Sitemap rigenerata dai contenuti attuali");
              } catch {
                toast.error("Rigenerazione sitemap non riuscita. Riprova tra poco.");
              } finally {
                setSitemapBusy(false);
              }
            }}
            disabled={sitemapBusy}
            title="Ricrea sitemap.xml dai contenuti attuali (home, pagine Soluzioni, pagine Clienti, legali)"
            className="inline-flex items-center gap-2 border border-royal/40 text-royal hover:bg-royal hover:text-white transition-colors text-sm font-semibold px-5 py-2.5 rounded-full disabled:opacity-60"
          >
            {sitemapBusy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {sitemapBusy ? "Rigenerazione…" : "Rigenera sitemap"}
          </button>
          <button
            data-testid="cms-regenerate-snapshot"
            onClick={regenerateSnapshot}
            disabled={regenBusy}
            title="Aggiorna la versione testuale del sito letta da Google e dai motori AI (ChatGPT, Perplexity…). Da premere dopo aver salvato modifiche ai contenuti."
            className="inline-flex items-center gap-2 border border-royal/40 text-royal hover:bg-royal hover:text-white transition-colors text-sm font-semibold px-5 py-2.5 rounded-full disabled:opacity-60"
          >
            {regenBusy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {regenBusy ? "Rigenerazione…" : "Rigenera snapshot SEO/AI"}
          </button>
          <button
            data-testid="cms-improve-all-button"
            onClick={improve}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white text-sm font-semibold px-5 py-2.5 rounded-full disabled:opacity-60"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            {busy ? "Miglioramento in corso…" : "Migliora con AI — applica tutti e salva"}
          </button>
        </div>
      </div>

      <div className="bg-surface border border-ice/8 rounded-xl p-6 mb-6 flex items-center gap-5">
        <Gauge size={22} className="text-azure shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">Score globale del sito</span>
            <span data-testid="cms-global-score" className={`font-display text-2xl font-extrabold ${data ? textColor(data.global) : "text-muted-foreground"}`}>
              {data ? data.global : "—"}
            </span>
          </div>
          <div className="h-2 rounded-full bg-onyx/80 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-700 ${data ? barColor(data.global) : ""}`} style={{ width: `${data?.global ?? 0}%` }} />
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {Object.entries(SECTION_LABELS).map(([key, label]) => {
          const s = data?.sections?.[key];
          return (
            <button
              key={key}
              data-testid={`cms-home-card-${key}`}
              onClick={() => onSelect(key)}
              className="group bg-surface border border-ice/8 rounded-xl p-5 text-left hover:border-royal/60 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-display font-semibold text-ice">{label}</span>
                <span className="inline-flex items-center gap-1 text-muted-foreground group-hover:text-azure transition-colors">
                  <span className={`font-mono text-sm font-semibold ${s ? textColor(s.score) : ""}`}>{s ? `${s.score}` : "—"}</span>
                  <ArrowRight size={14} />
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-onyx/80 overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${s ? barColor(s.score) : ""}`} style={{ width: `${s?.score ?? 0}%` }} />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{s ? `${s.ok}/${s.total} controlli superati` : "Caricamento…"}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
